import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Users,
  Shield,
  Calendar,
  Scale,
  Clock,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Bell,
  ChevronRight,
  Building,
  UserX,
  Gavel,
  FileWarning,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { formatDate } from '@/lib/utils';

// API-Funktion für Management-Dashboard
const fetchManagementDashboard = async () => {
  const token = localStorage.getItem('token');
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  const response = await fetch(`${API_URL}/api/v1/dashboard/management`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  
  if (!response.ok) {
    throw new Error('Fehler beim Laden des Dashboards');
  }
  
  return response.json();
};

// Fortschrittsbalken Komponente
function ProgressBar({
  value,
  max,
  color = 'primary',
}: {
  value: number;
  max: number;
  color?: 'primary' | 'green' | 'yellow' | 'red';
}) {
  const percentage = Math.min(100, (value / max) * 100);
  const colorClasses: Record<'primary' | 'green' | 'yellow' | 'red', string> = {
    primary: 'bg-primary',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };
  
  return (
    <div className="w-full bg-muted rounded-full h-3">
      <div 
        className={`h-3 rounded-full transition-all ${colorClasses[color] || colorClasses.primary}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

// Donut-Chart Komponente (vereinfacht)
function DonutChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24">
        <svg viewBox="0 0 36 36" className="w-24 h-24 transform -rotate-90">
          {data.reduce((acc, item, index) => {
            const prevTotal = data.slice(0, index).reduce((sum, d) => sum + d.value, 0);
            const startAngle = (prevTotal / total) * 100;
            const endAngle = (item.value / total) * 100;
            
            acc.push(
              <circle
                key={item.label}
                cx="18"
                cy="18"
                r="15.9"
                fill="none"
                stroke={item.color}
                strokeWidth="3"
                strokeDasharray={`${endAngle} ${100 - endAngle}`}
                strokeDashoffset={`${-startAngle}`}
              />
            );
            return acc;
          }, [] as JSX.Element[])}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold">{total}</span>
        </div>
      </div>
      <div className="space-y-1">
        {data.map(item => (
          <div key={item.label} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-muted-foreground">{item.label}:</span>
            <span className="font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManagementDashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['management-dashboard'],
    queryFn: fetchManagementDashboard,
    refetchInterval: 60000, // Alle 60 Sekunden aktualisieren
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Lädt Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-destructive">Fehler beim Laden des Dashboards</p>
        <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
      </div>
    );
  }

  if (!data) return null;

  const { occupancy, security, releasesTransfers, disciplinary, pendingDecisions, systemAlerts, personnelNote } = data;
  const overallStatus = (security.overallStatus as 'green' | 'yellow' | 'red') || 'green';

  // Status-Farbe für Sicherheit
  const securityStatusColor = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  }[overallStatus];

  const securityStatusText = {
    green: 'Keine kritischen Vorfälle',
    yellow: 'Erhöhte Aufmerksamkeit',
    red: 'Kritische Lage',
  }[overallStatus];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Dashboard Anstaltsleitung</h1>
        <p className="text-muted-foreground">
          Übersicht über die aktuelle Lage der Anstalt
        </p>
      </div>

      {/* System-Alerts */}
      {systemAlerts.length > 0 && systemAlerts.some((a: any) => a.type !== 'info') && (
        <div className="space-y-2">
          {systemAlerts.filter((a: any) => a.type !== 'info').map((alert: any, idx: number) => (
            <div 
              key={idx}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                alert.type === 'error' ? 'bg-red-50 border border-red-200' :
                alert.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                'bg-blue-50 border border-blue-200'
              }`}
            >
              {alert.type === 'error' ? (
                <AlertTriangle className="h-5 w-5 text-red-600" />
              ) : (
                <Bell className="h-5 w-5 text-amber-600" />
              )}
              <span className={alert.type === 'error' ? 'text-red-800' : 'text-amber-800'}>
                {alert.message}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* KPI-Kacheln */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Belegung */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Belegung
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{occupancy.occupiedPlaces}</div>
            <p className="text-sm text-muted-foreground">
              von {occupancy.totalCapacity} Plätzen ({occupancy.occupancyRate}%)
            </p>
            <ProgressBar 
              value={occupancy.occupiedPlaces} 
              max={occupancy.totalCapacity}
              color={occupancy.occupancyRate > 95 ? 'red' : occupancy.occupancyRate > 85 ? 'yellow' : 'green'}
            />
          </CardContent>
        </Card>

        {/* Sicherheitslage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Sicherheitslage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${securityStatusColor}`} />
              <span className="font-medium">{securityStatusText}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {security.incidents24h} Vorfälle (24h) • {security.highSecurityPrisonersCount} Hochsicherheit
            </p>
          </CardContent>
        </Card>

        {/* Entlassungen */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Entlassungen (30 Tage)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{releasesTransfers.releasesCount30d}</div>
            <p className="text-sm text-muted-foreground">
              {releasesTransfers.transfersCount30d} Verlegungen geplant
            </p>
          </CardContent>
        </Card>

        {/* Offene Entscheidungen */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Offene Entscheidungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{pendingDecisions.totalPending}</div>
            {pendingDecisions.overdueCount > 0 && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {pendingDecisions.overdueCount} überfällig
              </p>
            )}
            {pendingDecisions.overdueCount === 0 && (
              <p className="text-sm text-muted-foreground">
                Alle im Zeitrahmen
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Haupt-Bereiche */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Belegung nach Haftart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Belegung nach Haftart
            </CardTitle>
            <CardDescription>
              Verteilung der Gefangenen nach Vollzugsart
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DonutChart 
              data={occupancy.byCustodyType.map((item: any, idx: number) => ({
                label: item.label,
                value: item.count,
                color: ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][idx % 6],
              }))}
            />
          </CardContent>
        </Card>

        {/* Sicherheits-Flags */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Aktive Sicherheits-Flags
            </CardTitle>
            <CardDescription>
              Übersicht besonders zu sichernder Gefangener
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {security.activeSecurityFlags.length === 0 ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span>Keine aktiven Sicherheits-Flags</span>
                </div>
              ) : (
                security.activeSecurityFlags.map((flag: any) => (
                  <div key={flag.type} className="flex items-center justify-between">
                    <span className="text-sm">{flag.label}</span>
                    <Badge variant={flag.count > 5 ? 'destructive' : 'secondary'}>
                      {flag.count}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Anstehende Entlassungen */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5" />
              Anstehende Entlassungen
            </CardTitle>
            <CardDescription>
              Nächste 30 Tage
            </CardDescription>
          </CardHeader>
          <CardContent>
            {releasesTransfers.upcoming.length === 0 ? (
              <p className="text-muted-foreground text-sm">Keine Entlassungen in den nächsten 30 Tagen</p>
            ) : (
              <div className="space-y-2">
                {releasesTransfers.upcoming.slice(0, 5).map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <div>
                      <Link 
                        to={`/prisoners/${item.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {item.name}
                      </Link>
                      <p className="text-xs text-muted-foreground">{item.prisonerNumber}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatDate(item.date)}</p>
                      <Badge variant={item.type === 'release' ? 'default' : 'outline'} className="text-xs">
                        {item.type === 'release' ? 'Entlassung' : 'Verlegung'}
                      </Badge>
                    </div>
                  </div>
                ))}
                {releasesTransfers.upcoming.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center">
                    + {releasesTransfers.upcoming.length - 5} weitere
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Disziplinarmaßnahmen */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="h-5 w-5" />
              Disziplinarmaßnahmen
            </CardTitle>
            <CardDescription>
              Übersicht der letzten Verfahren
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{disciplinary.newCases7d}</div>
                <p className="text-xs text-muted-foreground">Neue (7 Tage)</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{disciplinary.newCases30d}</div>
                <p className="text-xs text-muted-foreground">Neue (30 Tage)</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">{disciplinary.openCases}</div>
                <p className="text-xs text-muted-foreground">Offen</p>
              </div>
            </div>
            {disciplinary.recentCases.length > 0 && (
              <div className="space-y-2 border-t pt-3">
                <p className="text-sm font-medium">Letzte Vorgänge</p>
                {disciplinary.recentCases.slice(0, 3).map((item: any) => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate max-w-[150px]">{item.category}</span>
                    <Badge variant={item.status === 'Abgeschlossen' ? 'default' : 'secondary'} className="text-xs">
                      {item.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Offene Entscheidungen Liste */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileWarning className="h-5 w-5" />
                Offene Entscheidungen der Leitung
              </CardTitle>
              <CardDescription>
                Vorgänge, die Ihre Entscheidung erfordern
              </CardDescription>
            </div>
            {pendingDecisions.overdueCount > 0 && (
              <Badge variant="destructive">
                {pendingDecisions.overdueCount} überfällig
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingDecisions.decisions.length === 0 ? (
            <div className="flex items-center gap-2 text-green-600 py-4">
              <CheckCircle className="h-5 w-5" />
              <span>Keine offenen Entscheidungen</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-2">Vorgangstyp</th>
                    <th className="pb-2">Gefangener</th>
                    <th className="pb-2">Eingangsdatum</th>
                    <th className="pb-2">Frist</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {pendingDecisions.decisions.slice(0, 10).map((decision: any) => (
                    <tr key={decision.id} className={`border-b ${decision.isOverdue ? 'bg-red-50' : ''}`}>
                      <td className="py-3 font-medium">{decision.type}</td>
                      <td className="py-3">
                        <Link 
                          to={`/prisoners/${decision.prisonerId}`}
                          className="text-primary hover:underline"
                        >
                          {decision.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">{decision.prisonerNumber}</p>
                      </td>
                      <td className="py-3 text-sm">{formatDate(decision.receivedDate)}</td>
                      <td className="py-3">
                        {decision.deadline && (
                          <span className={decision.isOverdue ? 'text-red-600 font-medium' : ''}>
                            {formatDate(decision.deadline)}
                            {decision.isOverdue && ' (überfällig)'}
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/prisoners/${decision.prisonerId}`}>
                            <ChevronRight className="h-4 w-4" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Personal-Hinweis */}
      {personnelNote && (
        <Card className="border-dashed">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Users className="h-5 w-5" />
              <div>
                <p className="font-medium">Personal & Dienste</p>
                <p className="text-sm">{personnelNote}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
